"use client";

import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import { ExternalLink, Menu } from "lucide-react";
import { MotionConfig } from "motion/react";

import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@workspace/ui/components/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@workspace/ui/components/sheet";
import { cn } from "@workspace/ui/lib/utils";

const SUPPORT_URL = "https://suporte.s2.solutionsoftwares.com.br/";
const INSTAGRAM_URL = "https://www.instagram.com/solutionsoftwares/";
const YOUTUBE_URL = "https://www.youtube.com/@GeoOfficeV2";
const LINKEDIN_URL = "https://www.linkedin.com/company/solution-softwares/";
const WHATSAPP_URL = "https://wa.me/5516988055633";

export function MarketingShell({
  children,
  activeProduct,
}: {
  children: ReactNode;
  activeProduct?: "desktop" | "platform";
}) {
  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
        <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
          <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-4 px-4 md:px-8 lg:px-10">
            <Link href="/" className="flex shrink-0 items-center gap-2.5">
              <ThemeAwareLogo size={30} priority />
              <span className="cn-font-heading text-lg font-semibold tracking-tight">
                GeoOffice
              </span>
            </Link>
            <nav
              className="ml-6 hidden items-center gap-1 lg:flex"
              aria-label="Navegação principal"
            >
              <HeaderLink href="/desktop" active={activeProduct === "desktop"}>
                GeoOffice V2
              </HeaderLink>
              <HeaderLink
                href="/platform"
                active={activeProduct === "platform"}
              >
                GeoOffice Platform
              </HeaderLink>
              <HeaderLink href="/#empresa">Empresa</HeaderLink>
              <a
                href={SUPPORT_URL}
                className="cursor-pointer rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                Suporte
              </a>
            </nav>
            <div className="ml-auto flex items-center gap-2">
              <ThemeToggle
                className="hidden w-fit sm:flex"
                itemClassName="flex-none"
              />
              <Button
                variant="ghost"
                className="hidden h-9 cursor-pointer px-3 sm:inline-flex"
                asChild
              >
                <Link href="/auth/login">Entrar</Link>
              </Button>
              <MobileMarketingMenu activeProduct={activeProduct} />
            </div>
          </div>
        </header>
        {children}
        <footer className="border-t border-border bg-card">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 lg:px-10">
            <div className="grid gap-10 border-b border-border pb-10 sm:grid-cols-2 lg:grid-cols-[1.35fr_0.8fr_1fr_1fr_1fr]">
              <div>
                <div className="flex items-center gap-2.5">
                  <ThemeAwareLogo size={28} />
                  <span className="cn-font-heading text-lg font-semibold">
                    GeoOffice
                  </span>
                </div>
                <p className="mt-4 max-w-xs text-sm leading-6 text-muted-foreground">
                  Solution Softwares desde 1999. Precisão para produzir.
                  Liberdade para trabalhar online.
                </p>
              </div>
              <FooterColumn title="Produtos">
                <Link href="/">Visão geral</Link>
                <Link href="/desktop">GeoOffice V2</Link>
                <Link href="/platform">GeoOffice Platform</Link>
              </FooterColumn>
              <FooterColumn title="Contato">
                <a href="https://wa.me/5516988055633">WhatsApp</a>
                <a href="mailto:vendas@solsoft.com.br">vendas@solsoft.com.br</a>
                <a href="mailto:suporte@solsoft.com.br">
                  suporte@solsoft.com.br
                </a>
                <a href={SUPPORT_URL}>Central de suporte</a>
              </FooterColumn>
              <FooterColumn title="Empresa">
                <span>Matão, SP</span>
                <Link href="/auth/login">Entrar</Link>
              </FooterColumn>
              <FooterColumn title="Redes sociais">
                <a href={LINKEDIN_URL}>LinkedIn</a>
                <a href={YOUTUBE_URL}>YouTube</a>
                <a href={INSTAGRAM_URL}>Instagram</a>
                <a href={WHATSAPP_URL}>WhatsApp</a>
              </FooterColumn>
            </div>
            <div className="flex flex-col gap-2 pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <p>
                © 2026 Solution Softwares Ltda. Todos os direitos reservados.
              </p>
              <p>GeoOffice é desenvolvido no Brasil.</p>
            </div>
          </div>
        </footer>
      </div>
    </MotionConfig>
  );
}

function MobileMarketingMenu({
  activeProduct,
}: {
  activeProduct?: "desktop" | "platform";
}) {
  const links = [
    { href: "/", label: "Visão geral" },
    { href: "/desktop", label: "GeoOffice V2", product: "desktop" },
    {
      href: "/platform",
      label: "GeoOffice Platform",
      product: "platform",
    },
    { href: "/#empresa", label: "Empresa" },
  ] as const;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="cursor-pointer lg:hidden"
          aria-label="Abrir navegação"
        >
          <Menu />
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Navegação</SheetTitle>
          <SheetDescription>
            Conheça os produtos e acesse sua conta GeoOffice.
          </SheetDescription>
        </SheetHeader>
        <nav
          className="flex flex-1 flex-col gap-1 p-5"
          aria-label="Navegação móvel"
        >
          {links.map((link) => {
            const active = "product" in link && link.product === activeProduct;
            return (
              <SheetClose asChild key={link.href}>
                <Link
                  href={link.href}
                  className={cn(
                    "rounded-md px-3 py-3 text-sm transition-colors",
                    active
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  {link.label}
                </Link>
              </SheetClose>
            );
          })}
          <a
            href={SUPPORT_URL}
            className="flex items-center justify-between rounded-md px-3 py-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            Suporte
            <ExternalLink className="size-4" />
          </a>
        </nav>
        <SheetFooter>
          <ThemeToggle className="w-full" itemClassName="flex-1" />
          <Button asChild>
            <Link href="/auth/login">Entrar</Link>
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function FooterColumn({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-2.5 text-sm text-muted-foreground [&_a]:transition-colors [&_a:hover]:text-foreground">
      <p className="mb-1 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-foreground">
        {title}
      </p>
      {children}
    </div>
  );
}

function HeaderLink({
  href,
  children,
  active = false,
}: {
  href: string;
  children: ReactNode;
  active?: boolean;
}) {
  return (
    <Link
      href={href}
      className={cn(
        "cursor-pointer rounded-md px-3 py-2 text-sm transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {children}
    </Link>
  );
}

function ThemeAwareLogo({
  size,
  priority = false,
}: {
  size: number;
  priority?: boolean;
}) {
  return (
    <>
      <Image
        src="/logo-platform-white.svg"
        alt=""
        width={size}
        height={size}
        priority={priority}
        className="hidden dark:block"
      />
      <Image
        src="/logo-platform.svg"
        alt=""
        width={size}
        height={size}
        className="dark:hidden"
      />
    </>
  );
}
